import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Button,
    Group,
    Loader,
    Modal,
    NumberInput,
    SegmentedControl,
    Select,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';

import { listVendorTermsOptions } from '@diagon-alley/common';

import { getEntityMasterTable } from '@api/entity-attributes';
import {
    listVendorPriceLines,
    listVendors,
    setVendorPrice,
    type VendorListItem,
    type VendorPriceLine,
} from '@api/vendors';
import { readApiError } from '@utils/apiError';

interface AddVendorPriceModalProps {
    opened: boolean;
    onClose: () => void;
    /** Fired after a successful save so the grid can reload. */
    onCreated: () => void;
}

type ItemKind = 'product' | 'material';

/** The `terms` value meaning "holds on any terms"; the server's own blank. */
const ANY_TERMS = '';

const SUGGESTED_CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'CNY', 'AED'];
const ITEM_PAGE_SIZE = 50;
/* Left-aligned under its input; the theme otherwise centres a narrow dropdown. */
const DROPDOWN = { width: 'target', position: 'bottom-start' } as const;

const todayIso = (): string => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatQty = (n: number): string => (n > 0 ? `from ${n.toLocaleString('en-IN')}` : 'any quantity');

/**
 * Adds one agreed price: a vendor, an item, the quantity it applies from, the payment terms it
 * is on, the currency and the figure. The everyday way to add a tier or a terms-specific price
 * without building a CSV. The server owns the rules: a combination that already has a current
 * price is a price change (the old line ends the day this one starts, and stays in the history),
 * and a start before the current price's is refused. The form shows the item's current lines so
 * that is visible before saving.
 */
const AddVendorPriceModal: React.FC<AddVendorPriceModalProps> = ({ opened, onClose, onCreated }) => {
    const [vendors, setVendors] = useState<VendorListItem[]>([]);
    const [vendorId, setVendorId] = useState<string | null>(null);
    const [kind, setKind] = useState<ItemKind>('product');
    const [itemId, setItemId] = useState<string | null>(null);
    const [itemSearch, setItemSearch] = useState('');
    const [itemOptions, setItemOptions] = useState<{ value: string; label: string }[]>([]);
    const [loadingItems, setLoadingItems] = useState(false);
    const [minQty, setMinQty] = useState<number | ''>(0);
    const [termsOptionId, setTermsOptionId] = useState<string>(ANY_TERMS);
    const [currency, setCurrency] = useState<string | null>(null);
    const [price, setPrice] = useState<number | ''>('');
    const [effectiveFrom, setEffectiveFrom] = useState(todayIso());
    const [note, setNote] = useState('');
    const [current, setCurrent] = useState<VendorPriceLine[]>([]);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const reset = () => {
        setVendorId(null);
        setKind('product');
        setItemId(null);
        setItemSearch('');
        setMinQty(0);
        setTermsOptionId(ANY_TERMS);
        setCurrency(null);
        setPrice('');
        setEffectiveFrom(todayIso());
        setNote('');
        setCurrent([]);
        setFieldErrors({});
        setSaveError(null);
        setSaving(false);
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    useEffect(() => {
        if (!opened) return;
        void listVendors()
            .then(setVendors)
            .catch(() => setVendors([]));
    }, [opened]);

    // Items are searched server-side, as on Add mapping: the catalogue is too big to load whole.
    useEffect(() => {
        if (!opened) return;
        let cancelled = false;
        setLoadingItems(true);
        if (searchTimer.current) clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            void getEntityMasterTable({
                entityType: kind === 'material' ? 'MATERIAL' : 'PRODUCT',
                page: 1,
                pageSize: ITEM_PAGE_SIZE,
                search: itemSearch.trim() || undefined,
                coreFilters: { isActive: ['true'] },
            })
                .then((result) => {
                    if (cancelled) return;
                    setItemOptions(
                        result.rows.map((row) => {
                            const id = String(row._entityKey ?? '');
                            const name = String(row.name ?? '');
                            return { value: id, label: name ? `${name} — ${id}` : id };
                        }),
                    );
                })
                .catch(() => {
                    if (!cancelled) setItemOptions([]);
                })
                .finally(() => {
                    if (!cancelled) setLoadingItems(false);
                });
        }, 300);
        return () => {
            cancelled = true;
            if (searchTimer.current) clearTimeout(searchTimer.current);
        };
    }, [opened, itemSearch, kind]);

    // The item's current lines with this vendor: shown, and used to default the currency.
    useEffect(() => {
        if (!opened || !vendorId || !itemId) {
            setCurrent([]);
            return;
        }
        let cancelled = false;
        void listVendorPriceLines({ vendorId, itemType: kind, itemId })
            .then((lines) => {
                if (cancelled) return;
                setCurrent(lines);
                if (lines.length > 0) setCurrency((c) => c ?? lines[0].currency);
            })
            .catch(() => {
                if (!cancelled) setCurrent([]);
            });
        return () => {
            cancelled = true;
        };
    }, [opened, vendorId, itemId, kind]);

    const vendor = vendors.find((v) => v.vendorId === vendorId) ?? null;
    const termsOptions = useMemo(() => listVendorTermsOptions(vendor?.paymentTermsConfig ?? null), [vendor]);
    const termsName = (id: string): string =>
        id === ANY_TERMS ? 'Any terms' : (termsOptions.find((o) => o.id === id)?.name ?? id);

    const currencyOptions = useMemo(() => {
        const seen = new Set<string>();
        return [...current.map((l) => l.currency), ...SUGGESTED_CURRENCIES].filter((c) => {
            if (seen.has(c)) return false;
            seen.add(c);
            return true;
        });
    }, [current]);

    // The line this save would replace, if the combination already has a current price.
    const replaces = current.find(
        (l) => l.currency === currency && Number(l.minQty) === Number(minQty || 0) && l.termsOptionId === termsOptionId,
    );

    const validate = (): boolean => {
        const errors: Record<string, string> = {};
        if (!vendorId) errors.vendorId = 'Choose a vendor';
        if (!itemId) errors.itemId = kind === 'material' ? 'Choose a material' : 'Choose a product';
        if (!currency) errors.currency = 'Choose the currency';
        if (price === '' || Number(price) <= 0) errors.price = 'Enter a price above zero';
        if (!/^\d{4}-\d{2}-\d{2}$/.test(effectiveFrom)) errors.effectiveFrom = 'Choose a start date';
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate() || !vendorId || !itemId || !currency || price === '') return;
        setSaving(true);
        setSaveError(null);
        try {
            await setVendorPrice({
                vendorId,
                itemType: kind,
                itemId,
                price: Number(price),
                currency,
                minQty: Number(minQty || 0),
                ...(termsOptionId !== ANY_TERMS ? { termsOptionId } : {}),
                effectiveFrom,
                ...(note.trim() ? { note: note.trim() } : {}),
            });
            onCreated();
            handleClose();
        } catch (err) {
            setSaveError(readApiError(err, 'Could not save the price. Please try again.'));
            setSaving(false);
        }
    };

    return (
        <Modal opened={opened} onClose={handleClose} title="Add price" size="md" centered>
            <Stack gap="md">
                <Alert color="blue" variant="light" py={8}>
                    <Text size="xs">Same tier, terms and currency replaces the current price.</Text>
                </Alert>

                <Select
                    label="Vendor"
                    placeholder="Choose a vendor"
                    data={vendors.map((v) => ({
                        value: v.vendorId,
                        label: v.name && v.name !== v.vendorId ? `${v.name} — ${v.vendorId}` : v.vendorId,
                    }))}
                    value={vendorId}
                    onChange={(v) => {
                        setVendorId(v);
                        setTermsOptionId(ANY_TERMS);
                    }}
                    error={fieldErrors.vendorId}
                    withAsterisk
                    searchable
                    comboboxProps={DROPDOWN}
                />

                <SegmentedControl
                    value={kind}
                    onChange={(v) => {
                        setKind(v as ItemKind);
                        setItemId(null);
                        setItemSearch('');
                    }}
                    data={[
                        { value: 'product', label: 'Product' },
                        { value: 'material', label: 'Material' },
                    ]}
                />

                <Select
                    label={kind === 'material' ? 'Material' : 'Product'}
                    placeholder={kind === 'material' ? 'Start typing a material name or code' : 'Start typing a product name or SKU'}
                    data={itemOptions}
                    value={itemId}
                    onChange={setItemId}
                    searchValue={itemSearch}
                    onSearchChange={setItemSearch}
                    error={fieldErrors.itemId}
                    rightSection={loadingItems ? <Loader size={14} /> : undefined}
                    nothingFoundMessage={loadingItems ? 'Searching…' : 'No match'}
                    withAsterisk
                    searchable
                    comboboxProps={DROPDOWN}
                />

                {current.length > 0 && (
                    <Stack gap={2}>
                        <Text size="xs" fw={600}>
                            Current prices for this item
                        </Text>
                        {current.map((l) => (
                            <Text key={l.id} size="xs" c="dimmed">
                                {Number(l.price)} {l.currency} · {formatQty(Number(l.minQty))} · {termsName(l.termsOptionId)} · since {l.effectiveFrom}
                            </Text>
                        ))}
                    </Stack>
                )}

                <Group grow align="flex-start">
                    <NumberInput
                        label="From quantity"
                        description="0 = any quantity"
                        value={minQty}
                        onChange={(v) => setMinQty(v === '' ? '' : Number(v))}
                        min={0}
                        allowNegative={false}
                    />
                    <Select
                        label="Payment terms"
                        description="Any = all options"
                        data={[
                            { value: ANY_TERMS, label: 'Any terms' },
                            ...termsOptions.map((o) => ({ value: o.id, label: o.name })),
                        ]}
                        value={termsOptionId}
                        onChange={(v) => setTermsOptionId(v ?? ANY_TERMS)}
                        allowDeselect={false}
                        comboboxProps={DROPDOWN}
                    />
                </Group>

                <Group grow align="flex-end">
                    <NumberInput
                        label="Price"
                        description="Per unit, before tax"
                        placeholder="24.64"
                        value={price}
                        onChange={(v) => setPrice(v === '' ? '' : Number(v))}
                        min={0}
                        decimalScale={6}
                        allowNegative={false}
                        error={fieldErrors.price}
                        withAsterisk
                    />
                    <Select
                        label="Currency"
                        data={currencyOptions}
                        value={currency}
                        onChange={setCurrency}
                        error={fieldErrors.currency}
                        withAsterisk
                        searchable
                        comboboxProps={DROPDOWN}
                    />
                </Group>

                <Group grow align="flex-start">
                    <TextInput
                        type="date"
                        label="Starts on"
                        value={effectiveFrom}
                        onChange={(e) => setEffectiveFrom(e.currentTarget.value)}
                        error={fieldErrors.effectiveFrom}
                        withAsterisk
                    />
                    <TextInput label="Note" placeholder="Optional" value={note} onChange={(e) => setNote(e.currentTarget.value)} />
                </Group>

                {replaces && (
                    <Alert color="yellow" variant="light">
                        <Text size="xs">
                            Replaces the current {Number(replaces.price)} {replaces.currency} ({formatQty(Number(replaces.minQty))},{' '}
                            {termsName(replaces.termsOptionId)}) from {effectiveFrom}. The old price stays in the history.
                        </Text>
                    </Alert>
                )}

                {saveError && (
                    <Alert color="red" variant="light">
                        <Text size="xs">{saveError}</Text>
                    </Alert>
                )}

                <Group justify="flex-end" gap="sm">
                    <Button variant="default" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button loading={saving} onClick={handleSubmit}>
                        {replaces ? 'Replace price' : 'Add price'}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};

export default AddVendorPriceModal;
